import { Injectable, inject } from '@angular/core';
import { PrintSettings } from '@shared/settings/print-settings.model';
import { BaseModel } from '@shared/models/base.model';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  private readonly _serviceKey = 'kfmPdf';
  private readonly _blazorService = inject(HsBlazorService);

  public async createTaskPdf(
    taskID: string,
    printingSettings: PrintSettings,
    showListPrice: boolean,
    download = false,
    fileName = 'task.pdf',
  ): Promise<void> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetProcessPDF',
      {
        id: taskID,
        printSettings: printingSettings,
      },
    );

    if (result) {
      const pdfStreamResult = (
        result as Record<
          string,
          string | Uint8Array<ArrayBufferLike> | undefined
        >
      )['pdfStream'];

      if (!download) {
        this.openPdf(pdfStreamResult);
      } else {
        this.downloadPdf(pdfStreamResult, fileName);
      }
    }
  }

  public async createOfferPdf(
    taskID: string,
    printingSettings: PrintSettings,
    showListPrice: boolean,
    download = false,
    fileName = 'offer.pdf',
  ): Promise<void> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetProcessPDF',
      {
        id: taskID,
        printSettings: printingSettings,
      },
    );

    if (result) {
      const pdfStreamResult = (
        result as Record<
          string,
          string | Uint8Array<ArrayBufferLike> | undefined
        >
      )['pdfStream'];

      if (!download) {
        this.openPdf(pdfStreamResult);
      } else {
        this.downloadPdf(pdfStreamResult, fileName);
      }
    }
  }

  public async createInvoicePdf(
    task: BaseModel,
    printingSettings: PrintSettings,
    showListPrice: boolean,
    download = false,
    fileName = 'invoice.pdf',
  ): Promise<void> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetProcessPDF',
      {
        id: task.uId,
        printSettings: printingSettings,
      },
    );

    if (result) {
      const pdfStreamResult = (
        result as Record<
          string,
          string | Uint8Array<ArrayBufferLike> | undefined
        >
      )['pdfStream'];

      if (!download) {
        this.openPdf(pdfStreamResult);
      } else {
        this.downloadPdf(pdfStreamResult, fileName);
      }
    }
  }

  public async createAppointmentPdf(
    appointmentId: string,
    download = false,
    fileName = 'appointment.pdf',
  ): Promise<void> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'GetAppointmentPDF',
      {
        id: appointmentId,
      },
    );

    if (result) {
      const pdfStreamResult = (
        result as Record<
          string,
          string | Uint8Array<ArrayBufferLike> | undefined
        >
      )['pdfStream'];

      if (!download) {
        this.openPdf(pdfStreamResult);
      } else {
        this.downloadPdf(pdfStreamResult, fileName);
      }
    }

    return undefined;
  }

  public createPdfBlob(pdfStream: Uint8Array): Blob {
    return new Blob([new Uint8Array(pdfStream)], { type: 'application/pdf' });
  }

  public createPdfBlobUrl(pdfStream: Uint8Array): string {
    const pdfBlob = this.createPdfBlob(pdfStream);
    const pdfUrl = URL.createObjectURL(pdfBlob);
    return pdfUrl;
  }

  public downloadPdf(pdf: string | Uint8Array | undefined, pdfName: string) {
    const isBlobUrl = pdf instanceof Uint8Array;
    const url = this.convertToUrl(pdf);
    // Create an anchor element
    const link = document.createElement('a');
    link.href = url;
    link.download = pdfName ?? 'document.pdf'; // Set the filename
    document.body.appendChild(link);
    link.click(); // Trigger download
    document.body.removeChild(link);
    if (isBlobUrl) {
      URL.revokeObjectURL(url);
    }
  }

  public openPdf(pdf: string | Uint8Array | undefined) {
    if (!pdf) {
      return;
    }

    const isBlobUrl = pdf instanceof Uint8Array;
    const url = this.convertToUrl(pdf);

    // Create an iframe to display the PDF
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow.print();
      // Clean up after printing dialog is closed/dismissed
      const cleanup = () => {
        document.body.removeChild(iframe);
        if (isBlobUrl) {
          URL.revokeObjectURL(url);
        }
        iframe.contentWindow?.removeEventListener('afterprint', cleanup);
      };
      iframe.contentWindow.addEventListener('afterprint', cleanup);
    };
  }

  private convertToUrl(pdf: string | Uint8Array | undefined): string {
    if (!pdf) {
      return '';
    }

    if (pdf instanceof Uint8Array) {
      return this.createPdfBlobUrl(pdf);
    } else {
      return pdf;
    }
  }
}
